import { useEffect, useState } from "react";
import { useMatchStore } from "@/stores/matchStore";
import { useSettings } from "@/stores/settingsStore";
import { HUD } from "@/constants/testIds";
import { WEAPON_ORDER, WEAPONS, getWeapon } from "@/game/weapons/weapons";
import { Heart, Shield, Circle, Timer } from "lucide-react";

export default function GameHUD() {
    const m = useMatchStore();
    const settings = useSettings();
    const [elapsed, setElapsed] = useState(0);
    const [fps, setFps] = useState(60);

    // Round timer (90s)
    useEffect(() => {
        setElapsed(0);
    }, [m.round]);

    useEffect(() => {
        if (m.phase !== "playing") return;
        const start = performance.now();
        const t = setInterval(() => setElapsed(Math.floor((performance.now() - start) / 1000)), 250);
        return () => clearInterval(t);
    }, [m.phase, m.round]);

    // FPS counter
    useEffect(() => {
        if (!settings.showFps) return;
        let frames = 0;
        let last = performance.now();
        let rafId;
        const loop = () => {
            frames++;
            const now = performance.now();
            if (now - last >= 500) {
                setFps(Math.round((frames * 1000) / (now - last)));
                frames = 0;
                last = now;
            }
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafId);
    }, [settings.showFps]);

    const roundTime = 90;
    const remaining = Math.max(0, roundTime - elapsed);
    const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
    const ss = String(remaining % 60).padStart(2, "0");

    return (
        <div data-testid={HUD.root} className="fixed inset-0 pointer-events-none z-10">
            {/* Crosshair */}
            <div
                className="crosshair"
                style={{
                    width: settings.crosshairSize,
                    height: settings.crosshairSize,
                    filter: `hue-rotate(${crosshairHue(settings.crosshairColor)}deg)`,
                }}
            />
            {/* Center dot */}
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                    width: 3,
                    height: 3,
                    background: settings.crosshairColor,
                    boxShadow: `0 0 6px ${settings.crosshairColor}`,
                }}
            />

            {/* Top center: score + timer */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 mt-4 flex items-center gap-6 axis-glass axis-cut px-6 py-2">
                <div className="flex flex-col items-center">
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/80">YOU</span>
                    <span data-testid={HUD.scoreYou} className="axis-hud text-3xl text-cyan-300">
                        {m.scoreYou}
                    </span>
                </div>
                <div className="flex flex-col items-center border-l border-r border-white/10 px-6">
                    <div className="flex items-center gap-1.5">
                        <Timer size={12} className="text-white/60" />
                        <span data-testid={HUD.timer} className="axis-hud text-2xl text-white tracking-wider">
                            {mm}:{ss}
                        </span>
                    </div>
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-white/50">ROUND {m.round}</span>
                </div>
                <div className="flex flex-col items-center">
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-red-400/80">ENEMY</span>
                    <span data-testid={HUD.scoreEnemy} className="axis-hud text-3xl text-red-400">
                        {m.scoreEnemy}
                    </span>
                </div>
            </div>

            {/* Bottom left: HP / Armor */}
            <div className="absolute bottom-6 left-6 axis-glass axis-cut px-5 py-3 min-w-[220px]">
                <div className="flex items-center gap-2 mb-1">
                    <Heart size={14} className="text-red-400" />
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-white/60">HEALTH</span>
                    <span data-testid={HUD.health} className="axis-hud text-2xl text-white ml-auto">
                        {Math.round(m.hp)}
                    </span>
                </div>
                <div className="h-1 bg-white/10 mb-2 axis-cut-sm overflow-hidden">
                    <div
                        className="h-full transition-all duration-200"
                        style={{
                            width: `${(m.hp / (m.maxHp || 100)) * 100}%`,
                            background: m.hp > 40 ? "linear-gradient(90deg, #00ff66, #00e5ff)" : "#ff2a2a",
                        }}
                    />
                </div>

                <div className="flex items-center gap-2 mb-1">
                    <Shield size={14} className="text-cyan-300" />
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-white/60">ARMOR</span>
                    <span data-testid={HUD.armor} className="axis-hud text-2xl text-white ml-auto">
                        {Math.round(m.armor)}
                    </span>
                </div>
                <div className="h-1 bg-white/10 axis-cut-sm overflow-hidden">
                    <div className="h-full bg-cyan-400 transition-all duration-200" style={{ width: `${m.armor}%` }} />
                </div>
            </div>

            {/* Bottom right: Ammo + Weapon inventory */}
            <div className="absolute bottom-6 right-6 axis-glass axis-cut px-5 py-3 min-w-[280px] text-right">
                <WeaponPanel />
            </div>

            {/* Top right: kill feed + FPS */}
            <div className="absolute top-6 right-6 flex flex-col items-end gap-2">
                {settings.showFps && (
                    <div data-testid={HUD.fps} className="axis-mono text-xs text-cyan-300/90 axis-glass px-2 py-1 axis-cut-sm">
                        FPS <span className="axis-hud text-lg ml-1">{fps}</span>
                    </div>
                )}
                <div className="space-y-1">
                    {m.killFeed.map((k) => (
                        <div key={k.id} className="axis-glass axis-cut-sm px-3 py-1 flex items-center gap-2 text-xs axis-fade-in">
                            <span className={`axis-heading tracking-widest ${k.killer === "YOU" ? "text-cyan-300" : "text-red-400"}`}>
                                {k.killer}
                            </span>
                            <span className="axis-mono text-white/50">[{k.weapon}]</span>
                            <span className={`axis-heading tracking-widest ${k.victim === "YOU" ? "text-red-400" : "text-white/70"}`}>
                                {k.victim}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Damage vignette */}
            <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                style={{
                    boxShadow: `inset 0 0 ${200 * (1 - m.hp / 100)}px ${60 * (1 - m.hp / 100)}px rgba(255,0,0,${
                        (1 - m.hp / 100) * 0.6
                    })`,
                }}
            />
        </div>
    );
}

function crosshairHue(hex) {
    // Rough hue-rotate for the default cyan crosshair CSS var
    if (hex === "#00e5ff") return 0;
    if (hex === "#00ff66") return -60;
    if (hex === "#ffb300") return 200;
    if (hex === "#ff2a2a") return 160;
    return 0;
}

function WeaponPanel() {
    const activeId = useMatchStore((s) => s.activeWeapon);
    const inventory = useMatchStore((s) => s.inventory);
    const switching = useMatchStore((s) => s.weaponSwitching);
    const switchWeapon = useMatchStore((s) => s.switchWeapon);
    const w = getWeapon(activeId);
    const inv = inventory[activeId];
    if (!inv) return null;

    return (
        <>
            <div className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/80">
                {w.displayName}
            </div>
            <div className="flex items-baseline justify-end gap-2 mt-1">
                <span data-testid={HUD.ammo} className="axis-hud text-5xl text-white leading-none">
                    {inv.ammo}
                </span>
                <span className="axis-hud text-2xl text-white/50">/ {inv.reserve}</span>
            </div>
            <div className="mt-2 h-1 bg-white/10 axis-cut-sm overflow-hidden">
                <div
                    className="h-full bg-cyan-400 transition-all duration-150"
                    style={{ width: `${(inv.ammo / inv.magSize) * 100}%` }}
                />
            </div>
            {inv.reloading && (
                <div className="mt-1 axis-mono text-[10px] tracking-[0.4em] text-amber-300 animate-pulse">
                    RELOADING...
                </div>
            )}

            {/* Weapon slots */}
            <div className="mt-3 flex items-center justify-end gap-2">
                {WEAPON_ORDER.map((id) => {
                    const wc = WEAPONS[id];
                    const wi = inventory[id];
                    const active = id === activeId;
                    return (
                        <button
                            key={id}
                            data-testid={`hud-weapon-${id}`}
                            onClick={() => switchWeapon(id)}
                            className={`relative axis-cut-sm px-3 py-1.5 border transition text-left pointer-events-auto ${
                                active
                                    ? "border-cyan-400 bg-cyan-400/10"
                                    : "border-white/10 bg-white/[0.02] hover:border-cyan-400/60"
                            }`}
                            title={`${wc.displayName} (${wc.slot})`}
                        >
                            <div className="flex items-center gap-2">
                                <span
                                    className="axis-mono text-[9px] tracking-[0.3em] px-1 py-px border"
                                    style={{
                                        borderColor: active ? wc.model.sightColor : "rgba(255,255,255,0.2)",
                                        color: active ? wc.model.sightColor : "rgba(255,255,255,0.5)",
                                    }}
                                >
                                    {wc.slot}
                                </span>
                                <div className="flex flex-col text-right">
                                    <span className={`axis-heading text-[10px] tracking-widest ${active ? "text-white" : "text-white/60"}`}>
                                        {id.toUpperCase()}
                                    </span>
                                    <span className="axis-mono text-[9px] text-white/40">
                                        {wi.ammo}/{wi.reserve}
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
            {switching && (
                <div className="mt-1 axis-mono text-[10px] tracking-[0.4em] text-cyan-300 animate-pulse">
                    SWITCHING...
                </div>
            )}
        </>
    );
}
